import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useSafeBack } from "../hooks/useSafeBack";
import {
  GraduationCap, ArrowLeft, TrendingUp, Target, Heart, ClipboardList,
  MessageSquare, Shield, Sparkles, Check, Users,
  Calendar, Award, ArrowRight, Briefcase, Building2, BookOpen, Lightbulb,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * TrainerPath — "Become A PDI Trainer" growth hub for ADI users.
 * Pure educational + CTA page. Links to existing supervisor + trainee tooling.
 */
export default function TrainerPath() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [pdiName, setPdiName] = useState("");
  const [pdiEmail, setPdiEmail] = useState("");
  const [pdiPhone, setPdiPhone] = useState("");
  const [deliveryChannels, setDeliveryChannels] = useState({ email: true, sms: false, whatsapp: false });
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [invites, setInvites] = useState([]);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/organisation/invites/mine`, { withCredentials: true });
      setInvites(data.items || []);
    } catch {
      setInvites([]);
    }
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const createPdiInvite = async () => {
    if (!pdiEmail.trim()) {
      toast.error("Enter the PDI's email");
      return;
    }
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/organisation/invites`,
        {
          email: pdiEmail.trim(),
          name: pdiName.trim() || null,
          phone: pdiPhone.trim() || null,
          delivery_channels: Object.entries(deliveryChannels)
            .filter(([, selected]) => selected)
            .map(([channel]) => channel),
        },
        { withCredentials: true },
      );
      setInvite(data);
      await loadInvites();
      toast.success(data.messages_sent ? "PDI activation sent · Business Growth Setup unlocked" : "Invitation created, but delivery needs attention");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't prepare the PDI invitation");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => goBack()}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Become A PDI Trainer</p>
            <p className="text-[10px] text-slate-400">Grow your career · mentor the next generation</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

        {/* ━━ HERO ━━ */}
        <div data-testid="trainer-hero"
          className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-700 to-indigo-800 rounded-3xl p-6 text-white">
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
          <div className="relative space-y-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-[10px] font-bold uppercase tracking-wider">
              <Award className="w-3 h-3" /> Career Growth Path
            </div>
            <h1 className="text-3xl font-extrabold leading-tight">From instructor to trainer.</h1>
            <p className="text-sm text-blue-100 leading-relaxed">
              You've spent years passing on your skill to pupils. Now it's time to pass it on to the
              next generation of instructors — and build something bigger than a diary full of lessons.
            </p>
          </div>
        </div>

        {/* ━━ WHY UPGRADE ━━ */}
        <Section title="Why Become A Trainer" Icon={TrendingUp} color="emerald">
          <BulletList items={[
            { icon: Briefcase, title: "New income stream", desc: "Trainers typically charge £35–£60/hour for supervised instruction on top of your own lessons." },
            { icon: Heart, title: "Mentor new instructors", desc: "Shape their early habits. Prevent the mistakes that cost pupils confidence." },
            { icon: Users, title: "Grow beyond solo lessons", desc: "Move from a one-person business into a team with capacity for dozens of pupils." },
            { icon: Building2, title: "Build your own driving school", desc: "Start with one trainee. Grow into a brand that pupils recommend." },
            { icon: Sparkles, title: "Use your experience", desc: "Every standards check you've passed is proof you can teach teachers." },
          ]} />
        </Section>

        {/* ━━ WHAT MAKES A GOOD TRAINER ━━ */}
        <Section title="What's Expected Of A Good Trainer" Icon={Target} color="violet">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Being a trainer isn't just teaching driving — it's teaching teaching. These qualities matter most:
          </p>
          <TraitGrid traits={[
            { label: "Communication", desc: "Explain why, not just what." },
            { label: "Patience", desc: "Trainees make different mistakes than pupils." },
            { label: "Coaching mindset", desc: "Ask questions first, give answers second." },
            { label: "Structured planning", desc: "Every lesson has a reason and an outcome." },
            { label: "Constructive feedback", desc: "Honest + supportive. Never one without the other." },
            { label: "Professionalism", desc: "You set the standard they'll copy." },
            { label: "Trainee confidence", desc: "Protect their belief — it's fragile in the first 40 hours." },
            { label: "Record keeping", desc: "Organised logs = faster Part 3 pass." },
          ]} />
        </Section>

        {/* ━━ ROADMAP ━━ */}
        <Section title="How To Become One" Icon={BookOpen} color="amber">
          <Roadmap steps={[
            { n: 1, title: "Build deep experience", body: "5+ years teaching + consistent Grade A–B standards check results. Confidence in your own method." },
            { n: 2, title: "Refine your standards", body: "Re-read the DVSA Part 3 form. Your lessons should already match those 17 competencies." },
            { n: 3, title: "Start with one trainee", body: "Taking on a single PDI lets you find your coaching rhythm without overwhelm." },
            { n: 4, title: "Use systems to stay organised", body: "Logs, competency scores, lesson plans, digital signatures — all in one place (see tools below)." },
            { n: 5, title: "Grow your reputation", body: "Happy pass-outs become your best advertising. Local PDIs will find you." },
          ]} />
        </Section>

        {/* ━━ PLATFORM TOOLS ━━ */}
        <Section title="Tools You Already Have On This Platform" Icon={Shield} color="blue">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            These trainer tools become ready when the PDI accepts the invitation you send:
          </p>
          <ToolList tools={[
            { Icon: Users, label: "Trainee dashboards", desc: "See readiness score, lesson count, trend, and recent debriefs at a glance." },
            { Icon: ClipboardList, label: "40-hour training log", desc: "Every debriefed lesson is auto-logged — no paperwork." },
            { Icon: Target, label: "17-competency tracking", desc: "Live Part 3 competency scoring from every debrief." },
            { Icon: TrendingUp, label: "Progress reports", desc: "Weekly & monthly readiness trends with colour-coded weak areas." },
            { Icon: Calendar, label: "Scheduling visibility", desc: "Optional calendar glance when your trainee allows it." },
            { Icon: MessageSquare, label: "Coaching notes & messaging", desc: "Leave structured notes (competency / lesson review / general)." },
          ]} />
        </Section>

        {/* ━━ GROWTH VISION ━━ */}
        <Section title="Your Growth Path" Icon={Building2} color="rose">
          <div className="relative pl-5 space-y-4">
            <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-slate-200 dark:bg-slate-700" />
            {[
              { stage: "Stage 1", title: "One trainee", desc: "Single PDI. You find your voice, build your first pass story." },
              { stage: "Stage 2", title: "Multiple trainees", desc: "3–5 PDIs. You're now a named trainer in your area." },
              { stage: "Stage 3", title: "Team growth", desc: "Trained PDIs who pass Part 3 become your associate instructors. You earn on their lessons too." },
              { stage: "Stage 4", title: "Premium brand", desc: "Local name. Training school. Multi-six-figure operation built on real quality." },
            ].map((s, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[18px] top-1 w-4 h-4 rounded-full bg-rose-500 ring-4 ring-rose-100 dark:ring-rose-900/30" />
                <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">{s.stage}</p>
                <p className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5">{s.title}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ━━ READINESS SELF-CHECK ━━ */}
        <div data-testid="readiness-check" className="bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-900/15 dark:to-fuchsia-900/10 border border-violet-200 dark:border-violet-800 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-violet-600" />
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Ready right now?</p>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            If you can honestly tick these, you're ready to take your first trainee:
          </p>
          <ul className="space-y-1.5">
            {[
              "Consistently Grade A or B on your last 3 standards checks",
              "Comfortable explaining the 17 DVSA competencies",
              "Happy to watch a lesson without taking over",
              "Enjoy writing structured feedback (not just bullet-point lesson notes)",
              "Organised enough to track another person's development for 40+ hours",
            ].map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />{c}
              </li>
            ))}
          </ul>
        </div>

        {/* ━━ INVITE FIRST PDI ━━ */}
        <section
          data-testid="invite-pdi-panel"
          className="bg-white dark:bg-slate-800 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-5 space-y-4"
        >
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
              Your next level
            </p>
            <h2 className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">
              Invite a PDI
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Sending the invitation opens Coach/Educator setup and your Business Growth Setup.
              The PDI creates their account, chooses their plan and pays through the secure activation journey.
            </p>
          </div>

          {invites.length > 0 && (
            <div data-testid="pdi-invite-history" className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <p className="text-xs font-extrabold text-slate-800 dark:text-slate-100">Your PDI invitations</p>
              {invites.map((item) => (
                <div key={item.id} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-900">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{item.intended_name || item.intended_email}</p>
                    <p className="truncate text-[11px] text-slate-500">{item.intended_email}</p>
                    <p className="mt-1 font-mono text-[11px] text-slate-500">{item.code}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${item.status === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    {item.status === "accepted" ? "Active" : "Pending acceptance"}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
              PDI name
              <input
                value={pdiName}
                onChange={(event) => setPdiName(event.target.value)}
                placeholder="e.g. John Cook"
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
              PDI email
              <input
                type="email"
                value={pdiEmail}
                onChange={(event) => setPdiEmail(event.target.value)}
                placeholder="name@example.com"
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300 sm:col-span-2">
              Mobile number <span className="font-normal text-slate-400">(needed for SMS or WhatsApp)</span>
              <input
                type="tel"
                value={pdiPhone}
                onChange={(event) => setPdiPhone(event.target.value)}
                placeholder="07… or +44…"
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </label>
          </div>

          <fieldset className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
            <legend className="px-1 text-xs font-bold text-slate-700 dark:text-slate-300">Send by</legend>
            <div className="grid grid-cols-3 gap-2">
              {[
                ["email", "Email"],
                ["sms", "SMS"],
                ["whatsapp", "WhatsApp"],
              ].map(([channel, label]) => (
                <label key={channel} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={deliveryChannels[channel]}
                    onChange={(event) => setDeliveryChannels((current) => ({
                      ...current,
                      [channel]: event.target.checked,
                    }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <Button
            type="button"
            data-testid="create-pdi-invite"
            onClick={createPdiInvite}
            disabled={inviting || !pdiEmail.trim()}
            className="h-11 w-full rounded-xl bg-indigo-600 text-sm font-bold text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {inviting ? "Sending invitation…" : "Send PDI activation"}
          </Button>

          {invite ? (
            <div
              data-testid="pdi-invite-result"
              className="rounded-xl border border-emerald-700/40 bg-emerald-50 p-4 dark:bg-emerald-950/20"
            >
              <p className="text-sm font-extrabold text-emerald-800 dark:text-emerald-300">
                Business Growth Setup unlocked
              </p>
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-200">
                Activation code: <span className="font-mono font-bold">{invite.code}</span>
              </p>
              <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
                {Object.entries(invite.delivery?.results || {}).map(([channel, result]) => (
                  <p key={channel}>
                    <span className="font-bold capitalize">{channel}:</span>{" "}
                    {result.provider_accepted ? "accepted by provider" : result.status || "not sent"}
                  </p>
                ))}
              </div>
              <Button
                type="button"
                onClick={() => nav("/onboarding/wizard")}
                className="mt-3 h-10 w-full rounded-lg bg-emerald-700 text-xs font-bold text-white hover:bg-emerald-600"
              >
                Continue Business Growth Setup
              </Button>
            </div>
          ) : null}
        </section>

        {/* ━━ CTA ━━ */}
        <div data-testid="trainer-cta" className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-6 text-white space-y-3">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-indigo-400" />
            <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Ready to start</p>
          </div>
          <h2 className="text-xl font-extrabold">Invite your first trainee.</h2>
          <p className="text-xs text-slate-300 leading-relaxed">
            Send a directed activation. The PDI registers, pays for their chosen plan and completes setup.
            Acceptance activates supervision and unlocks their operational tools.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <Button data-testid="open-supervisor" onClick={() => nav("/supervisor")}
              className="h-11 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-bold">
              Open Supervisor Portal <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button data-testid="back-to-dashboard" onClick={() => nav("/instructor")}
              variant="outline" className="h-11 bg-transparent border-slate-600 text-white hover:bg-slate-700 rounded-xl text-sm font-bold">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──
function Section({ title, Icon, color, children }) {
  const colorMap = {
    emerald: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
    violet: "bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400",
    amber: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    rose: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400",
  };
  return (
    <section className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className={`w-8 h-8 rounded-xl ${colorMap[color]} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="text-base font-extrabold text-slate-900 dark:text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function BulletList({ items }) {
  return (
    <div className="space-y-3">
      {items.map((it, i) => {
        const I = it.icon;
        return (
          <div key={i} className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
              <I className="w-4 h-4 text-slate-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900 dark:text-white">{it.title}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{it.desc}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TraitGrid({ traits }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {traits.map((t, i) => (
        <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
          <p className="text-xs font-extrabold text-slate-900 dark:text-white">{t.label}</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{t.desc}</p>
        </div>
      ))}
    </div>
  );
}

function Roadmap({ steps }) {
  return (
    <div className="space-y-3">
      {steps.map(s => (
        <div key={s.n} className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-extrabold flex items-center justify-center flex-shrink-0">
            {s.n}
          </div>
          <div className="flex-1 pt-1">
            <p className="text-sm font-bold text-slate-900 dark:text-white">{s.title}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{s.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ToolList({ tools }) {
  return (
    <div className="space-y-2">
      {tools.map((t, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-700/40 px-3 py-2.5">
          <t.Icon className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-900 dark:text-white">{t.label}</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t.desc}</p>
          </div>
          <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}
